import { DOCUMENT } from '@angular/common'
import {
  Component,
  HostListener,
  Inject,
  isDevMode,
  OnDestroy,
  OnInit
} from '@angular/core'
import gql from 'graphql-tag'
import { Observable, Subject } from 'rxjs'
import { filter, map, switchMap, takeUntil } from 'rxjs/operators'

import { Router } from '@angular/router'
import { DataService } from './data/providers/data.service'
import { FirebaseService } from './data/providers/firebase.service'
import { ServerConfigService } from './data/server-config'
import { LocalStorageService } from './providers/local-storage/local-storage.service'
import { NotificationService } from './providers/notification/notification.service'
import { NotificationTypeEntity } from './public_api'

export const GET_CITIES = gql`
  query Cities {
    cities(options: { filter: { code: { notEq: "00" } } }) {
      totalItems
      items {
        id
        createdAt
        updatedAt
        code
        name
        nameEn
        fullName
        fullNameEn
        codeName
        wards {
          id
          createdAt
          updatedAt
          code
          name
          nameEn
          fullName
          fullNameEn
          codeName
        }
      }
    }
  }
`

export const GET_DISTRICTS = gql`
  query Districts($cityCode: String!) {
    districts(cityCode: $cityCode) {
      totalItems
      items {
        id
        createdAt
        updatedAt
        code
        name
        nameEn
        fullName
        fullNameEn
        codeName
      }
    }
  }
`

export const GET_WARDS = gql`
  query Wards($districtCode: String!) {
    wards(districtCode: $districtCode) {
      items {
        id
        createdAt
        updatedAt
        code
        name
        nameEn
        fullName
        fullNameEn
        codeName
      }
    }
  }
`

export const GET_PROFILE_ADMIN_ESCAPE = gql`
  query GetProfileAdminEscape {
    profileMerchantManager {
      id
      name
      email
      phone
      address
      positionInSystem
      createdAt
      updatedAt
      isAdmin
      imageUrl
      imageId
      merchantId
      venueId
      userId
      hotelId
      baseId
      merchantType
      merchant {
        businessName
        representative
        taxCode
        email
        businessCode
        contactPerson
        contactPhone
      }
      merchant_venue_id
    }
  }
`

@Component({
  selector: 'vdr-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  loading$: Observable<boolean>
  private _document?: Document
  private destroy$ = new Subject<void>()

  constructor(
    private dataService: DataService,
    private serverConfigService: ServerConfigService,
    private localStorageService: LocalStorageService,
    private firebaseService: FirebaseService,
    private notificationService: NotificationService,
    private router: Router,
    @Inject(DOCUMENT) private document?: any
  ) {
    this._document = document
  }

  ngOnInit() {
    this.firebaseService.onMessageReceived
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload) => {
        if (!payload || !this.firebaseService.hasPushNotificationPermission) {
          return
        }

        const title = payload?.notification?.title || 'Bạn có thông báo mới'
        const body = payload?.notification?.body
        const icon = payload?.notification?.icon || '/assets/img/logo-login.png'

        let link = '#'

        try {
          // Parse data.message nếu có (dạng JSON string)
          const rawData = payload?.data?.message
            ? JSON.parse(payload.data.message)
            : payload.data

          switch (rawData?.type) {
            case NotificationTypeEntity.BOOKING_HOTEL:
              link = `/admin/orders-hotel/${rawData.hotelId}/${rawData.bookingHotelId}`

              break

            case NotificationTypeEntity.RECONCILIATION_DETAIL:
            case NotificationTypeEntity.RECONCILIATION_PERIOD:
            case NotificationTypeEntity.RECONCILIATION_PERIOD_LIST:
              link = `/admin/revenue-hotel/commission/${rawData.hotelId}`
              break

            default:
              link = '#'
              break
          }
        } catch (err) {
          console.error('❌ Lỗi parse message từ payload:', err)
        }

        const notification = new Notification(title, {
          body,
          icon,
          silent: true
        })

        notification.onclick = () => {
          window.focus()
          if (link && link !== '#') {
            window.location.href = link
          }
        }
      })

    this.loading$ = this.dataService.client
      .getNetworkStatus()
      .stream$.pipe(map((data) => 0 < data.networkStatus.inFlightRequests))

    this.dataService.client
      .uiState()
      .mapStream((data) => data.uiState.theme)
      .subscribe((theme) => {
        this._document?.body.setAttribute('data-theme', theme)
        this._document?.body.setAttribute(
          'cds-theme',
          theme === 'dark' ? 'dark' : 'light'
        )
      })

    // Once logged in, keep the localStorage "contentLanguageCode" in sync with the
    // uiState. Also perform a check to ensure that the current contentLanguage is
    // one of the availableLanguages per GlobalSettings.
    this.dataService.client
      .userStatus()
      .mapStream(({ userStatus }) => userStatus.isLoggedIn)
      .pipe(
        filter((loggedIn) => loggedIn === true),
        switchMap(() =>
          this.dataService.client
            .uiState()
            .mapStream((data) => data.uiState.contentLanguage)
        ),
        switchMap((contentLang) =>
          this.serverConfigService
            .getAvailableLanguages()
            .pipe(map((available) => [contentLang, available] as const))
        )
      )
      .subscribe({
        next: ([contentLanguage, availableLanguages]) => {
          this.localStorageService.set('contentLanguageCode', contentLanguage)
          if (
            availableLanguages.length &&
            !availableLanguages.includes(contentLanguage)
          ) {
            this.dataService.client
              .setContentLanguage(availableLanguages[0])
              .subscribe()
          }
        }
      })

    this.dataService.client.userStatus().stream$.subscribe(({ userStatus }) => {
      this.localStorageService.setAdminId(userStatus.administratorId)

      const previousAdminId = this.localStorageService.get('currentAdminId')
      const currentAdminId = userStatus.administratorId

      // Clear profile if user changed or logged out
      if (previousAdminId !== currentAdminId) {
        this.localStorageService.remove('userProfile')
        if (currentAdminId) {
          this.localStorageService.set('currentAdminId', currentAdminId)
        }
      }

      if (userStatus.administratorId) {
        const theme = this.localStorageService.get('activeTheme')
        if (theme) {
          this.dataService.client.setUiTheme(theme).subscribe(() => {
            this.localStorageService.set('activeTheme', theme)
          })
        }
        const activeChannelToken =
          this.localStorageService.get('activeChannelToken')
        if (activeChannelToken) {
          const activeChannel = userStatus.channels.find(
            (c) => c.token === activeChannelToken
          )
          if (activeChannel) {
            this.dataService.client
              .setActiveChannel(activeChannel.id)
              .subscribe()
          }
        }

        // Fetch user profile if not already in local storage
        const existingProfile = this.localStorageService.get('userProfile')
        if (!existingProfile && userStatus.administratorId) {
          this.dataService
            .query<any>(GET_PROFILE_ADMIN_ESCAPE)
            .mapStream((data) => data.profile_AdminEscape)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (profile) => {
                if (profile) {
                  this.localStorageService.set('userProfile', profile)
                }
              },
              error: (error) => {
                console.error('Failed to fetch user profile:', error)
              }
            })
        } else {
          // User logged out, clear profile data
          this.localStorageService.remove('userProfile')
          this.localStorageService.remove('currentAdminId')
        }
      }
    })

    this.loadCitiesAsync()
    this.initializeFCM()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalHotkeys(event: KeyboardEvent) {
    if (
      (event.ctrlKey === true || event.metaKey === true) &&
      event.key === 'u'
    ) {
      event.preventDefault()
      if (isDevMode()) {
        this.dataService.client
          .uiState()
          .single$.pipe(
            switchMap(({ uiState }) =>
              this.dataService.client.setDisplayUiExtensionPoints(
                !uiState.displayUiExtensionPoints
              )
            )
          )
          .subscribe()
      }
    }
  }

  private async initializeFCM() {
    const isEnabled = await this.firebaseService.requestPermission()

    if (isEnabled) {
      this.setupGlobalMessageListener()
    }
  }

  /**
   * Setup global message listener for Firebase Cloud Messaging
   */
  private setupGlobalMessageListener() {
    this.firebaseService
      .listenForMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (payload) => {
          this.handleNotification(payload)
        },
        error: (error) => {
          console.error('Error listening for messages:', error)
        }
      })
  }

  /**
   * Handle incoming notifications based on type and current app state
   */
  private handleNotification(payload: any) {
    this.broadcastNotificationEvent(payload)

    const currentUrl = this.router.url

    // Only show notification toast if not already on the relevant page
    const isOnChatPage = currentUrl.includes('/chat')
    if (!isOnChatPage) {
      // Show notification with action
      this.notificationService.notify({
        message: 'Bạn có tin nhắn mới',
        type: 'info',
        duration: 3000,
        action: () => {
          this.router.navigate(['/chat'])
        }
      })
    }
  }

  private broadcastNotificationEvent(payload: any): void {
    window.dispatchEvent(
      new CustomEvent('merchant-notification-received', {
        detail: payload
      })
    )
  }

  /**
   * Load cities data in background without blocking the main flow
   */
  private loadCitiesAsync(): void {
    // Check if cities data already exists in localStorage
    const existingCities = this.localStorageService.get(
      'administrativeDivision'
    )

    if (
      existingCities &&
      Array.isArray(existingCities) &&
      existingCities.length <= 34
    ) {
      // Cities data already exists, no need to fetch again
      console.log('Cities data already exists in localStorage, skipping fetch')
      return
    }

    // Fire and forget - don't wait for completion
    this.dataService
      .query<any>(GET_CITIES)
      .mapStream((data) => data)
      .pipe(
        map((data) => data),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (data) => {
          if (data?.cities?.items) {
            this.localStorageService.set(
              'administrativeDivision',
              data.cities.items
            )
            console.log('Cities data loaded and saved to localStorage')
          }
        },
        error: (error) => {
          console.error('Error loading cities:', error)
        }
      })
  }
}
